import React from 'react';
import {
  ChevronDownIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { SlugBadge, TenantStatusBadge, PlanTierBadge } from './PlatformBadges';
import { TenantUsersTable, TenantUserRow } from './TenantUsersTable';
import { TenantQuickActions } from './TenantQuickActions';
import { TenantActionMenu } from './TenantActionMenu';

export interface PlatformTenant {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  contactName: string | null;
  contactEmail: string | null;
  phone: string | null;
  notes: string | null;
  userCount: number;
  organizationId?: string | null;
  organizationName?: string | null;
  planTier?: string | null;
  planLabel?: string | null;
  createdAt: string;
}

interface TenantAccordionItemProps {
  tenant: PlatformTenant;
  open: boolean;
  onToggle: () => void;
  users: TenantUserRow[];
  usersLoading: boolean;
  onEdit: () => void;
  onViewDetail: () => void;
  onCreateAdmin: () => void;
  onToggleTenantStatus: () => void;
  onImpersonateTenant: () => void;
  onResetAdminPassword: () => void;
  onResendCredentials: () => void;
  onViewIncidents: () => void;
  onViewAudit: () => void;
  onToggleUserStatus: (userId: string) => void;
  onResetPassword: (userId: string) => void;
  onDeleteUser: (user: TenantUserRow) => void;
  toggleUserPending?: boolean;
}

const formatCreated = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('es', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

export const TenantAccordionItem: React.FC<TenantAccordionItemProps> = ({
  tenant: t,
  open,
  onToggle,
  users,
  usersLoading,
  onEdit,
  onViewDetail,
  onCreateAdmin,
  onToggleTenantStatus,
  onImpersonateTenant,
  onResetAdminPassword,
  onResendCredentials,
  onViewIncidents,
  onViewAudit,
  onToggleUserStatus,
  onResetPassword,
  onDeleteUser,
  toggleUserPending,
}) => (
  <li className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm transition-shadow duration-200 hover:border-slate-300 hover:shadow-md">
    <div
      className={clsx(
        'flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        open && 'border-b border-slate-100 bg-slate-50/50'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-start gap-3 text-left transition-colors hover:bg-slate-50/80 sm:items-center rounded-xl sm:-m-2 sm:p-2"
      >
        <span
          className={clsx(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm transition-transform duration-200 sm:mt-0',
            open && 'rotate-0 border-slate-300 bg-slate-100'
          )}
        >
          <ChevronDownIcon
            className={clsx('h-5 w-5 text-slate-500 transition-transform duration-200', open ? 'rotate-180' : 'rotate-[-90deg]')}
            aria-hidden
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <BuildingOffice2Icon className="hidden h-4 w-4 text-slate-400 sm:inline" aria-hidden />
            <span className="truncate text-base font-semibold text-slate-900">{t.name}</span>
            <SlugBadge>{t.slug}</SlugBadge>
            <TenantStatusBadge isActive={t.isActive} />
            {t.planLabel ? <PlanTierBadge tier={t.planTier} label={t.planLabel} /> : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1 font-medium text-slate-600">
              <span className="tabular-nums font-semibold text-slate-800">{t.userCount}</span>
              usuario{t.userCount !== 1 ? 's' : ''}
            </span>
            {t.contactEmail && (
              <span className="truncate max-w-[220px]" title={t.contactEmail}>
                {t.contactEmail}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-slate-400">
              <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden />
              Alta {formatCreated(t.createdAt)}
            </span>
          </div>
        </div>
      </button>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 pt-3 sm:border-t-0 sm:pt-0">
        <TenantQuickActions onViewDetail={onViewDetail} onCreateAdmin={onCreateAdmin} />
        <TenantActionMenu
          tenant={t}
          onEdit={onEdit}
          onToggleStatus={onToggleTenantStatus}
          onImpersonate={onImpersonateTenant}
          onResetPassword={onResetAdminPassword}
          onResendCredentials={onResendCredentials}
          onCreateAdmin={onCreateAdmin}
          onViewIncidents={onViewIncidents}
          onViewAudit={onViewAudit}
        />
      </div>
    </div>

    <div
      className={clsx(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="border-t border-slate-100 bg-gradient-to-b from-slate-50/90 to-slate-50 px-4 pb-5 pt-4">
          {t.notes && (
            <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 text-sm text-amber-950">
              <span className="font-semibold text-amber-900">Notas comerciales · </span>
              {t.notes}
            </div>
          )}
          {usersLoading ? (
            <div className="space-y-3 py-4">
              <div className="h-10 animate-pulse rounded-lg bg-slate-200/80" />
              <div className="h-24 animate-pulse rounded-xl bg-slate-200/60" />
            </div>
          ) : users.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white/80 py-10 text-center">
              <p className="text-sm font-medium text-slate-700">Sin usuarios en este espacio</p>
              <p className="mt-1 text-xs text-slate-500">Añade un administrador con la acción rápida «Admin».</p>
            </div>
          ) : (
            <TenantUsersTable
              users={users}
              onToggleStatus={onToggleUserStatus}
              onResetPassword={onResetPassword}
              onDelete={onDeleteUser}
              togglePending={toggleUserPending}
            />
          )}
        </div>
      </div>
    </div>
  </li>
);
